/**
 * The FAQs for `/tools/thread-splitter`, in their own module.
 *
 * A route's `head` is never code-split, so importing this list from the tool
 * component would pull the whole component into the shared entry chunk that
 * every visitor to every page downloads — the trap `marketing-perf-ceiling`
 * documents, and one the Mastodon limit checker was caught by.
 *
 * Rendered visibly on the page AND emitted as `FAQPage` JSON-LD from the route.
 */
export const THREAD_SPLITTER_FAQS = [
  {
    q: "What is the character limit for a thread on Bluesky, X, Mastodon and Threads?",
    a: "Each post in the chain gets the network's normal limit: 300 characters on Bluesky, 280 on X, and 500 on Mastodon and Threads. A thread does not raise the per-post limit — it just chains several posts together, so a 2,000-word draft becomes a chain rather than one long post.",
  },
  {
    q: "Do links count against the character limit?",
    a: "Yes, but not at their real length. X and Mastodon both charge a flat 23 characters for a link however long the URL is, so a 200-character tracking link costs the same as a short one. Bluesky counts a link's actual characters. This page applies each network's rule, which is why a draft full of links splits into fewer posts here than a plain character count suggests.",
  },
  {
    q: "How many posts can one thread have?",
    a: "There is no hard cap on the networks themselves, but 25 is the practical ceiling most tools use — PostDaily included — and it is where a thread stops being readable. This page will split past 25 and tell you it has, rather than silently truncating your draft.",
  },
  {
    q: "Does this change my wording?",
    a: "No. It splits on whole words and never rewrites, reorders or summarises anything — the text in each post is your text. One caveat worth knowing: runs of whitespace inside a paragraph are collapsed to single spaces, so deliberate indentation or blank lines within a segment do not survive. Use a line containing only --- to force a break exactly where you want one.",
  },
  {
    q: "Why does this count differently from the box I usually type in?",
    a: "Because most editors count the characters in the string, and no network does. X weights emoji and CJK characters as two and links as 23; Bluesky counts grapheme clusters, so a family emoji is one character rather than eleven, and enforces a separate 3,000-byte ceiling underneath; Mastodon makes the server half of a remote mention free. This page uses the same counting functions our publisher uses.",
  },
] as const;
